import { useEffect, useMemo, useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Seed, SeedTag, accentName, seedFromDate, seedLabel } from "./components/Seed";

const EMAIL = "contacto@randev.com.ar";
const INSTAGRAM = "https://www.instagram.com/randev.ar/";
const LINKEDIN = "https://www.linkedin.com/company/randevarg";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const SERVICIOS = [
  {
    seed: 62,
    nombre: "Sistemas a medida",
    texto: "Turnos, pedidos, stock, facturación, reservas. La herramienta que hoy resolvés con planillas y mensajes, hecha a la forma de tu negocio.",
  },
  {
    seed: 37,
    nombre: "Webs",
    texto: "Sitios rápidos, claros y pensados para el celular, que dicen qué hacés y llevan a la gente a escribirte.",
  },
  {
    seed: 90,
    nombre: "Apps móviles",
    texto: "Apps para iOS y Android, para tus clientes o para tu equipo en la calle, con notificaciones y pensadas para usarse en movimiento.",
  },
  {
    seed: 104,
    nombre: "Integraciones",
    texto: "Conectamos lo que ya usás: WhatsApp, Mercado Pago, ARCA, Google Sheets. Menos copiar y pegar, menos errores.",
  },
];

const PASOS = [
  { titulo: "Escuchamos", texto: "Una charla para entender cómo trabajás hoy, qué te frena y qué tendría que cambiar." },
  { titulo: "Proponemos", texto: "Alcance, etapas, plazos y presupuesto por escrito. Sabés qué vas a recibir antes de empezar." },
  { titulo: "Construimos", texto: "Entregas cada dos semanas que podés probar con tu equipo. Ajustamos sobre algo real, no sobre un documento." },
  { titulo: "Acompañamos", texto: "Lanzamos con vos, capacitamos a quien lo use y seguimos con soporte y mejoras." },
];

// El footer va sobre tinta, así que usa la versión invertida en cada tema.
function Logo({ invertido = false }) {
  const claro = "/marca/randev-horizontal.svg";
  const oscuro = "/marca/randev-horizontal-negativo.svg";
  return (
    <picture>
      <source srcSet={invertido ? claro : oscuro} media="(prefers-color-scheme: dark)" />
      <img src={invertido ? oscuro : claro} alt="randev" className="logo" width="132" height="30" />
    </picture>
  );
}

function Nav() {
  return (
    <header className="nav">
      <div className="wrap nav-in">
        <a href="#top" className="nav-logo" aria-label="randev, inicio"><Logo /></a>
        <nav className="nav-links" aria-label="Secciones">
          <a href="#que-hacemos">Qué hacemos</a>
          <a href="#como-trabajamos">Cómo trabajamos</a>
          <a href="#tu-semilla">Tu semilla</a>
        </nav>
        <a href="#contacto" className="btn btn--primario btn--chico">Contanos<span className="nav-cta-extra"> tu proyecto</span></a>
      </div>
    </header>
  );
}

function Hero({ hoy }) {
  return (
    <section className="hero" id="top">
      <div className="wrap hero-in">
        <div className="hero-txt">
          <p className="etiqueta">Software a medida · Jujuy, Argentina</p>
          <h1 className="display"><span>nada</span> <span>al azar</span></h1>
          <p className="subtitulo">
            Hacemos el sistema que tu negocio usa todos los días. Webs, apps y herramientas internas,
            pensadas para cómo trabajás vos.
          </p>
          <div className="hero-cta">
            <a href="#contacto" className="btn btn--primario">Contanos tu proyecto <ArrowRight size={16} strokeWidth={1.5} /></a>
            <a href="#como-trabajamos" className="btn btn--secundario">Cómo trabajamos</a>
          </div>
        </div>
        <figure className="hero-seed">
          <Seed seed={1} animate className="hero-seed-svg" />
          <figcaption><SeedTag seed={1}>randev</SeedTag></figcaption>
        </figure>
      </div>
      <div className="wrap hero-hoy">
        <Seed seed={hoy} size={28} />
        <p>
          La semilla de hoy es la <strong>{seedLabel(hoy)}</strong>. Cada proyecto que arranca recibe la suya
          y la lleva hasta el lanzamiento.
        </p>
      </div>
    </section>
  );
}

function Servicios() {
  return (
    <section className="seccion" id="que-hacemos">
      <div className="wrap">
        <div className="seccion-head">
          <p className="etiqueta">Qué hacemos</p>
          <h2 className="titulo">Software hecho para un negocio, no para todos</h2>
        </div>
        <ul className="servicios">
          {SERVICIOS.map((s) => (
            <li key={s.seed} className="servicio">
              <Seed seed={s.seed} size={88} />
              <div className="servicio-txt">
                <SeedTag seed={s.seed} />
                <h3>{s.nombre}</h3>
                <p>{s.texto}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Proceso() {
  return (
    <section className="seccion seccion--hondo" id="como-trabajamos">
      <div className="wrap">
        <div className="seccion-head">
          <p className="etiqueta">Cómo trabajamos</p>
          <h2 className="titulo">Cuatro etapas, sin sorpresas</h2>
          <p className="subtitulo">Lo aleatorio se controla con método. Así es el nuestro.</p>
        </div>
        <ol className="pasos">
          {PASOS.map((p, i) => (
            <li key={p.titulo} className="paso">
              <span className="paso-n">{String(i + 1).padStart(2, "0")}</span>
              <h3>{p.titulo}</h3>
              <p>{p.texto}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const toInputValue = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function TuSemilla({ fecha, setFecha }) {
  const date = useMemo(() => {
    const [y, m, d] = fecha.split("-").map(Number);
    return y && m && d ? new Date(y, m - 1, d) : new Date();
  }, [fecha]);
  const seed = seedFromDate(date);

  return (
    <section className="seccion" id="tu-semilla">
      <div className="wrap semilla-in">
        <div className="semilla-txt">
          <p className="etiqueta">Tu semilla</p>
          <h2 className="titulo">Tu proyecto ya tiene número</h2>
          <p className="cuerpo">
            En programación, lo aleatorio se controla con una semilla: el mismo número da siempre el mismo
            resultado. Cada proyecto de randev recibe la suya el día que arranca, y ese símbolo lo acompaña
            en la propuesta, en el código y en el lanzamiento.
          </p>
          <label className="campo" htmlFor="fecha-inicio">
            <span className="etiqueta">¿Cuándo arrancamos?</span>
            <input
              id="fecha-inicio"
              type="date"
              value={fecha}
              onChange={(e) => e.target.value && setFecha(e.target.value)}
            />
          </label>
          <p className="semilla-res">
            Si arrancamos el {date.getDate()} de {MESES[date.getMonth()]}, tu proyecto es el{" "}
            <strong>{seedLabel(seed)}</strong>, en {accentName(seed)}.
          </p>
          <a href="#contacto" className="btn btn--primario">Arranquemos ese día <ArrowRight size={16} strokeWidth={1.5} /></a>
        </div>
        <figure className="semilla-fig">
          <Seed key={seed} seed={seed} animate className="semilla-svg" />
          <figcaption className="semilla-cap"><SeedTag seed={seed}>tu proyecto</SeedTag></figcaption>
        </figure>
      </div>
    </section>
  );
}

function Contacto({ fecha }) {
  const [gracias, setGracias] = useState(false);
  useEffect(() => {
    if (window.location.hash === "#gracias") {
      setGracias(true);
      document.getElementById("contacto")?.scrollIntoView();
    }
  }, []);
  const [y, m, d] = fecha.split("-").map(Number);
  const seed = seedFromDate(new Date(y, m - 1, d));

  return (
    <section className="seccion" id="contacto">
      <div className="wrap contacto-in">
        <div className="contacto-txt">
          <p className="etiqueta">Contacto</p>
          <h2 className="titulo">Contanos qué necesitás</h2>
          <p className="cuerpo">
            Respondemos en menos de 24 horas. La primera charla no tiene costo: si no somos los indicados,
            te lo decimos.
          </p>
          <dl className="datos">
            <div><dt>Email</dt><dd>{EMAIL}</dd></div>
            <div><dt>Base</dt><dd>San Salvador de Jujuy, Argentina</dd></div>
            <div><dt>Redes</dt><dd>
              <a href={INSTAGRAM} target="_blank" rel="noreferrer">Instagram <ArrowUpRight size={14} strokeWidth={1.5} /></a>
              <a href={LINKEDIN} target="_blank" rel="noreferrer">LinkedIn <ArrowUpRight size={14} strokeWidth={1.5} /></a>
            </dd></div>
          </dl>
        </div>
        {gracias ? (
          <div className="form gracias" role="status">
            <Seed seed={seed} size={96} animate />
            <h3 className="titulo">Recibimos tu mensaje</h3>
            <p className="cuerpo">Te escribimos en menos de 24 horas al email que dejaste.</p>
          </div>
        ) : (
          <form className="form" action={`https://formsubmit.co/${EMAIL}`} method="POST">
            <input type="hidden" name="_next" value="https://randev.com.ar/#gracias" />
            <input type="hidden" name="_subject" value={`Nuevo proyecto ${seedLabel(seed)} desde randev.com.ar`} />
            <input type="hidden" name="_captcha" value="true" />
            <input type="hidden" name="_template" value="table" />
            <input type="hidden" name="semilla" value={seedLabel(seed)} />
            <div className="form-fila">
              <label className="campo" htmlFor="f-nombre">
                <span className="etiqueta">Nombre</span>
                <input id="f-nombre" name="name" type="text" autoComplete="name" required />
              </label>
              <label className="campo" htmlFor="f-email">
                <span className="etiqueta">Email</span>
                <input id="f-email" name="email" type="email" autoComplete="email" required />
              </label>
            </div>
            <div className="form-fila">
              <label className="campo" htmlFor="f-tel">
                <span className="etiqueta">Teléfono (opcional)</span>
                <input id="f-tel" name="phone" type="tel" autoComplete="tel" />
              </label>
              <label className="campo" htmlFor="f-servicio">
                <span className="etiqueta">Qué necesitás</span>
                <select id="f-servicio" name="service" defaultValue="Sistema a medida">
                  <option>Sistema a medida</option>
                  <option>Web</option>
                  <option>App móvil</option>
                  <option>Integración</option>
                  <option>Todavía no sé</option>
                </select>
              </label>
            </div>
            <label className="campo" htmlFor="f-mensaje">
              <span className="etiqueta">Tu proyecto</span>
              <textarea id="f-mensaje" name="message" rows="5" required placeholder="Qué hace tu negocio y qué te gustaría resolver." />
            </label>
            <div className="form-pie">
              <SeedTag seed={seed}>se guarda con tu mensaje</SeedTag>
              <button type="submit" className="btn btn--primario">Enviar <ArrowRight size={16} strokeWidth={1.5} /></button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap footer-in">
        <div className="footer-marca">
          <Logo invertido />
          <p>Software a medida desde Jujuy. Nada al azar.</p>
        </div>
        <nav className="footer-links" aria-label="Pie">
          <a href="#que-hacemos">Qué hacemos</a>
          <a href="#como-trabajamos">Cómo trabajamos</a>
          <a href="#contacto">Contacto</a>
          <a href={INSTAGRAM} target="_blank" rel="noreferrer">Instagram</a>
          <a href={LINKEDIN} target="_blank" rel="noreferrer">LinkedIn</a>
        </nav>
        <p className="footer-legal">© {new Date().getFullYear()} randev · {EMAIL}</p>
      </div>
    </footer>
  );
}

export default function App() {
  const hoy = useMemo(() => new Date(), []);
  const [fecha, setFecha] = useState(toInputValue(hoy));
  return (
    <>
      <Nav />
      <main>
        <Hero hoy={seedFromDate(hoy)} />
        <Servicios />
        <Proceso />
        <TuSemilla fecha={fecha} setFecha={setFecha} />
        <Contacto fecha={fecha} />
      </main>
      <Footer />
    </>
  );
}
